<template>
  <Page :title="batch?.name ?? 'Map columns'">
    <template #actions>
      <span
        v-if="saveLabel"
        class="self-center text-sm"
        :class="saveError ? 'text-red-600' : 'text-gray-500'"
        data-cy="map-save-status"
      >
        <i
          v-if="saveLabel === 'Saved'"
          class="fas fa-check mr-1 text-green-600"
          aria-hidden="true"
        />{{ saveLabel }}
      </span>
      <SeraButton
        v-if="canReplaceFile"
        text="Replace file"
        type="secondary"
        data-cy="map-replace-file"
        @click="replacingFile = true"
      />
    </template>

    <ImportSteps :current="1" class="mb-6" />

    <div v-if="loading" class="flex justify-center py-10">
      <Spinner message="Loading..." class="text-gray-500" />
    </div>

    <div v-else-if="pageError" data-cy="map-page-error">
      <p class="text-gray-700">{{ PAGE_ERRORS[pageError] }}</p>
      <a
        href="/imports"
        class="text-sm text-blue-600 hover:underline"
        data-cy="map-back-to-imports"
        @click.prevent="router.push('/imports')"
        >Back to imports</a
      >
    </div>

    <template v-else-if="batch && mapping">
      <SummaryStrip :items="summaryItems" class="mb-4" />

      <Alert
        v-if="banner.length"
        type="warning"
        class="mb-4"
        data-cy="map-banner"
      >
        <p v-for="message in banner" :key="message">{{ message }}</p>
      </Alert>
      <Alert
        v-if="droppedText"
        type="warning"
        :text="droppedText"
        class="mb-4"
        data-cy="map-dropped-fields"
      />
      <Alert
        v-if="errorText"
        type="error"
        :text="errorText"
        class="mb-4"
        data-cy="map-error"
      />

      <div
        v-if="mode === 'failed'"
        class="mb-4 flex flex-wrap items-center justify-between gap-3"
        data-cy="map-failed"
      >
        <Alert
          type="warning"
          class="flex-1"
          text="This load failed. Re-mapping clears the rows it staged."
        />
        <SeraButton
          text="Re-map"
          :saving="saving"
          data-cy="map-remap"
          @click="confirmRemap = true"
        />
      </div>
      <p
        v-else-if="mode === 'locked' && !locked"
        class="mb-4 text-sm text-gray-600"
        data-cy="map-locked"
      >
        This import can no longer be re-mapped.
      </p>

      <NavTab
        class="mb-4"
        variant="underline"
        :tabs="navTabs"
        @select="selectTab"
      />

      <MapColumnRows
        :rows="visibleRows"
        :options-for="optionsFor"
        :disabled="!editable"
        :mobile="isMobile"
        :sample-text="sampleLabel(mapping.sample)"
        :sample-disabled="!mapping.sample?.count || saving || sampling"
        @pick="pick"
        @next-sample="nextSample"
      />

      <div class="mt-4 flex justify-end gap-2" data-cy="map-footer">
        <SeraButton
          text="Back"
          outline
          data-cy="map-back"
          @click="router.push('/imports')"
        />
        <SeraButton
          text="Continue"
          :disabled="!canContinue"
          :saving="starting"
          data-cy="map-continue"
          @click="startLoad"
        />
      </div>
    </template>

    <Modal
      v-if="confirmEdit"
      :model-value="true"
      title="Edit mapping?"
      size="small"
      @update:model-value="cancelEdit"
    >
      <p class="text-gray-700">
        Editing the mapping reloads the file and discards the dry run.
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 p-4">
          <SeraButton
            text="Cancel"
            type="secondary"
            data-cy="map-edit-cancel"
            @click="cancelEdit"
          />
          <SeraButton
            text="Edit mapping"
            :saving="resetting"
            data-cy="map-edit-confirm"
            @click="confirmReset"
          />
        </div>
      </template>
    </Modal>

    <ReplaceFileModal
      v-if="batch?.replace_file && batch.links.replace_file"
      :open="replacingFile"
      :upload="batch.replace_file"
      :url="batch.links.replace_file"
      @cancel="replacingFile = false"
      @replaced="fileReplaced"
    />

    <Modal
      v-if="confirmRemap"
      :model-value="true"
      title="Re-map this batch?"
      size="small"
      @update:model-value="confirmRemap = false"
    >
      <p class="text-gray-700">
        The rows this load staged are cleared. The file is loaded again when you
        start the load.
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 p-4">
          <SeraButton
            text="Cancel"
            type="secondary"
            data-cy="map-remap-cancel"
            @click="confirmRemap = false"
          />
          <SeraButton
            text="Re-map"
            data-cy="map-remap-confirm"
            @click="remap"
          />
        </div>
      </template>
    </Modal>
  </Page>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Page from "@/components/Page.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import NavTab from "@/components/Seranova/NavTab/NavTab.vue";
import Spinner from "@/components/Spinner.vue";
import { useBreakpoints } from "@/composables/useBreakpoints";
import ImportSteps from "@/DataImports/flow/ImportSteps.vue";
import MapColumnRows from "@/DataImports/flow/map/MapColumnRows.vue";
import {
  blockerMessages,
  filterRows,
  hasNoRows,
  MAP_TABS,
  type MapImport,
  type MapOption,
  type MapRow,
  type MapTabKey,
  nextSampleIndex,
  NO_ROWS_MESSAGE,
  responseMessage,
  sampleLabel,
  tabCounts,
  withState
} from "@/DataImports/flow/map/mapColumns";
import ReplaceFileModal from "@/DataImports/flow/ReplaceFileModal.vue";
import SummaryStrip from "@/DataImports/flow/SummaryStrip.vue";
import { tabLabel } from "@/DataImports/importsHubRows";
import {
  availableFields,
  buildRows,
  type MappingResponse,
  withColumn
} from "@/DataImports/mappingRows";

const PAGE_ERRORS = {
  not_found: "Import not found",
  forbidden: "You don't have access to imports.",
  failed: "Couldn't load the import. Refresh to try again."
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { type } = useBreakpoints();
const isMobile = computed(() => type.value === "mobile");

const loading = ref(true);
const pageError = ref<keyof typeof PAGE_ERRORS | null>(null);
const batch = ref<MapImport | null>(null);
const mapping = ref<MappingResponse | null>(null);
const fields = ref<Record<string, string>>({});
const activeTab = ref<MapTabKey>("all");

const saving = ref(false);
const dirty = ref(false);
const saved = ref(false);
const saveError = ref("");
// A 409 from the server means the load moved on; the pickers stay off after it.
const locked = ref(false);
const starting = ref(false);
const startError = ref("");
const startBlockers = ref<string[]>([]);
const sampling = ref(false);
const sampleError = ref("");
const confirmEdit = ref(false);
const confirmRemap = ref(false);
const resetting = ref(false);
const replacingFile = ref(false);
const pendingPick = ref<{ header: string; fieldKey: string | null } | null>(
  null
);

// `reset`: loaded, so the first change must reload the file. `failed`: a failed load, re-mapped via its own button.
const mode = computed<"edit" | "reset" | "failed" | "locked">(() => {
  const current = mapping.value;
  if (!current || !batch.value || locked.value) return "locked";
  if (current.awaiting_mapping) return "edit";
  if (batch.value.status === "load_failed" && current.remappable)
    return "failed";
  const canReset =
    batch.value.can?.edit_mapping && batch.value.links.reset_mapping;
  return batch.value.status === "ready" && canReset ? "reset" : "locked";
});
const editable = computed(
  () => mode.value === "edit" || mode.value === "reset"
);

const allFields = computed(() =>
  mapping.value ? availableFields(mapping.value.registry, fields.value) : []
);
const rows = computed<MapRow[]>(() => {
  const current = mapping.value;
  if (!current) return [];
  const sampleRow = current.sample?.values ?? current.sample_rows?.[0];
  return buildRows(current.columns, sampleRow, fields.value).map((row) =>
    withState(
      row,
      current.preview.find((item) => item.key === row.fieldKey)
    )
  );
});
const counts = computed(() => tabCounts(rows.value));
const visibleRows = computed(() => filterRows(rows.value, activeTab.value));
const mappedCount = computed(
  () => rows.value.filter((row) => row.fieldKey).length
);

const navTabs = computed(() =>
  MAP_TABS.map(({ key, label }) => ({
    label: `${label} (${counts.value[key]})`,
    active: key === activeTab.value
  }))
);

const summaryItems = computed(() => [
  { label: "File", value: batch.value?.files?.[0]?.file_name },
  { label: "Record type", value: tabLabel(batch.value?.record_type ?? null) },
  { label: "Vendor", value: batch.value?.vendor },
  { label: "Rows", value: batch.value?.rows },
  {
    label: "Columns",
    value: `${mappedCount.value} of ${rows.value.length} matched`
  }
]);

const noRows = computed(() => !!mapping.value && hasNoRows(mapping.value));

const banner = computed(() => {
  if (noRows.value) return [NO_ROWS_MESSAGE];
  if (startBlockers.value.length) return startBlockers.value;
  return mapping.value
    ? blockerMessages(mapping.value, mapping.value.registry)
    : [];
});
const canReplaceFile = computed(
  () =>
    !!batch.value?.can?.replace_file &&
    !!batch.value.replace_file &&
    !!batch.value.links.replace_file &&
    !saving.value &&
    !starting.value
);
const droppedText = computed(() => {
  const dropped = mapping.value?.dropped_fields ?? [];
  if (!dropped.length) return "";
  const list = dropped.map((field) => `${field.column} (${field.label})`);
  return `Not in the new file: ${list.join(", ")}.`;
});

const errorText = computed(
  () => saveError.value || startError.value || sampleError.value
);

const saveLabel = computed(() => {
  if (saving.value || dirty.value) return "Saving…";
  if (saveError.value) return "Not saved";
  return saved.value ? "Saved" : "";
});

const canContinue = computed(
  () =>
    mode.value === "edit" &&
    !noRows.value &&
    !!mapping.value?.startable &&
    !!batch.value?.links.start_load &&
    !saving.value &&
    !dirty.value &&
    !saveError.value &&
    !starting.value
);

const usedBy = computed(
  () =>
    new Map(
      rows.value
        .filter((row) => row.fieldKey)
        .map((row) => [row.fieldKey as string, row.index])
    )
);

function optionsFor(row: MapRow): MapOption[] {
  return allFields.value
    .filter((field) => {
      const owner = usedBy.value.get(field.key);
      return owner === undefined || owner === row.index;
    })
    .map((field) => ({
      value: field.key,
      label:
        field.requirement === "required"
          ? `${field.label} (required)`
          : field.label,
      help: field.help
    }));
}

function selectTab(tab: { label: string }) {
  const selected = navTabs.value.findIndex(({ label }) => label === tab.label);
  if (selected >= 0) activeTab.value = MAP_TABS[selected].key;
}

function applyMapping(data: MappingResponse) {
  mapping.value = data;
}

// One save in flight at a time; the newest choices are re-sent when it lands.
async function save() {
  if (saving.value) {
    dirty.value = true;
    return;
  }
  saving.value = true;
  dirty.value = false;
  try {
    const res = await store.dispatch("saveColumnMapping", {
      url: batch.value!.links.mapping,
      fields: fields.value,
      sample: mapping.value?.sample?.index ?? null
    });
    saveError.value = "";
    saved.value = true;
    if (!dirty.value) applyMapping(res.data);
  } catch (error: any) {
    saveError.value = responseMessage(error, "The mapping could not be saved.");
    if (error?.response?.status === 409) locked.value = true;
  } finally {
    saving.value = false;
    if (dirty.value && !locked.value) save();
  }
}

function applyPick(header: string, fieldKey: string | null) {
  startBlockers.value = [];
  startError.value = "";
  fields.value = withColumn(fields.value, header, fieldKey);
  save();
}

function pick(row: MapRow, value: unknown) {
  if (!editable.value) return;
  const fieldKey = value ? String(value) : null;
  if (mode.value === "reset") {
    pendingPick.value = { header: row.header, fieldKey };
    confirmEdit.value = true;
    return;
  }
  applyPick(row.header, fieldKey);
}

async function refreshBatch() {
  try {
    const { data } = await store.dispatch("fetchImport", {
      batchType: route.params.batchType,
      id: route.params.id
    });
    batch.value = data;
  } catch {
    // The links already held still work; only Continue needs the refreshed ones.
  }
}

function cancelEdit() {
  confirmEdit.value = false;
  pendingPick.value = null;
}

async function confirmReset() {
  resetting.value = true;
  try {
    const res = await store.dispatch(
      "resetColumnMapping",
      batch.value!.links.reset_mapping
    );
    applyMapping(res.data);
    fields.value = { ...res.data.mapping };
    await refreshBatch();
    const pending = pendingPick.value;
    if (pending) applyPick(pending.header, pending.fieldKey);
  } catch (error: any) {
    saveError.value = responseMessage(
      error,
      "The mapping could not be edited."
    );
  } finally {
    resetting.value = false;
    cancelEdit();
  }
}

// The batch is back to mapping on the new file; reload what the page shows from it.
async function fileReplaced() {
  replacingFile.value = false;
  locked.value = false;
  saveError.value = "";
  startError.value = "";
  startBlockers.value = [];
  try {
    await refreshBatch();
    const res = await store.dispatch("getColumnMappingSample", {
      url: batch.value!.links.mapping,
      sample: null
    });
    applyMapping(res.data);
    fields.value = { ...res.data.mapping };
  } catch (error: any) {
    saveError.value = responseMessage(error, "The new file could not be read.");
  }
}

async function remap() {
  confirmRemap.value = false;
  await save();
  await refreshBatch();
}

async function nextSample() {
  const current = mapping.value?.sample;
  if (!current || saving.value || sampling.value) return;
  sampling.value = true;
  sampleError.value = "";
  try {
    const res = await store.dispatch("getColumnMappingSample", {
      url: batch.value!.links.mapping,
      sample: nextSampleIndex(current.index, current.count)
    });
    applyMapping(res.data);
  } catch (error: any) {
    sampleError.value = responseMessage(
      error,
      "The sample could not be loaded."
    );
  } finally {
    sampling.value = false;
  }
}

async function startLoad() {
  starting.value = true;
  startError.value = "";
  startBlockers.value = [];
  try {
    await store.dispatch(
      "startColumnMappedLoad",
      batch.value!.links.start_load
    );
    router.push(batch.value!.links.review_page);
  } catch (error: any) {
    starting.value = false;
    const data = error?.response?.data;
    const blockers =
      error?.response?.status === 409 && data
        ? blockerMessages(data, mapping.value!.registry)
        : [];
    if (blockers.length) startBlockers.value = blockers;
    else
      startError.value = responseMessage(
        error,
        "The load could not be started."
      );
  }
}

const pendingSave = computed(() => saving.value || dirty.value);
const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = "";
};

// Leaving mid-save would drop the newest pick without a word.
watch(pendingSave, (active) => {
  if (active) window.addEventListener("beforeunload", warnBeforeUnload);
  else window.removeEventListener("beforeunload", warnBeforeUnload);
});

onBeforeRouteLeave(
  () =>
    !pendingSave.value ||
    window.confirm("Your last change is still saving. Leave anyway?")
);

onBeforeUnmount(() =>
  window.removeEventListener("beforeunload", warnBeforeUnload)
);

onMounted(async () => {
  try {
    const { data } = await store.dispatch("fetchImport", {
      batchType: route.params.batchType,
      id: route.params.id
    });
    batch.value = data;
    if (!data.links.mapping) {
      // The server drops the mapping links once the import starts, so a reload lands on Review.
      if (data.links.review_page) await router.replace(data.links.review_page);
      else pageError.value = "not_found";
      return;
    }
    const res = await store.dispatch("getColumnMappingSample", {
      url: data.links.mapping,
      sample: null
    });
    applyMapping(res.data);
    fields.value = { ...res.data.mapping };
    saved.value = Object.keys(fields.value).length > 0;
    store.dispatch("setBreadcrumbs", [
      { label: "Imports", url: "/imports" },
      { label: data.name }
    ]);
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 404) pageError.value = "not_found";
    else if (status === 403) pageError.value = "forbidden";
    else pageError.value = "failed";
  } finally {
    loading.value = false;
  }
});
</script>
